/**
 * Number formatting that matches Centerline for Mac byte-for-byte.
 *
 * - `NumberFormatter` (en_US, decimal style) rounds half-to-even, so we use
 *   Intl with `roundingMode: "halfEven"`.
 * - `String(format: "%.2f")` (printf) rounds the exact binary value with ties
 *   to even. Intl also operates on the exact binary value, so the same option
 *   reproduces printf.
 */

const cache = new Map<string, Intl.NumberFormat>();

function formatter(decimals: number, grouping: boolean): Intl.NumberFormat {
  const key = `${decimals}:${grouping}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: grouping,
      // ES2023 Intl.NumberFormat v3; supported by Node 20+ and evergreen browsers.
      roundingMode: "halfEven",
    } as Intl.NumberFormatOptions);
    cache.set(key, f);
  }
  return f;
}

/** `QuickMeasureFormat.number`: grouped, fixed decimals. 1234.5 → "1,234.50". */
export function groupedNumber(value: number, decimals: number): string {
  return formatter(decimals, true).format(value);
}

/** printf `%.Nf` (no grouping). */
export function printfFixed(value: number, decimals: number): string {
  return formatter(decimals, false).format(value);
}

/** Centerline's `compactNumber`/`compact`: whole → "%.0f", else "%.2f". */
export function compactNumber(value: number): string {
  return Math.round(value) === value ? printfFixed(value, 0) : printfFixed(value, 2);
}
